import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import LazyImage from 'dibs-react-lazy-image';
import { trackArticleClick } from 'dibs-editorial-module/exports/tracking.ts';
import {
    WORDPRESS_THUMB,
    WORDPRESS_MEDIUM,
    WORDPRESS_FULL,
} from '../../constants/imageConstants.js';
import { INITIAL_VISIBLE_INTERIOR_ARTICLES } from '../../constants/sbConstants.ts';
import classnames from 'classnames';

import styles from './SbSharedInteriorsArticles.scss';
import dibsCss from 'dibs-css';

import { type SbSharedInteriorsArticles_editorial$data } from './__generated__/SbSharedInteriorsArticles_editorial.graphql.ts';

const LAZY_IMAGE_VERTICAL_OFFSET = 200;
const getHeaderId = (index: number): string => `wp-article-header-${index}`;

type Props = {
    imgSizes: string;
    editorial: SbSharedInteriorsArticles_editorial$data | null | undefined;
    isExpanded?: boolean;
};

const SbSharedInteriorsArticlesComponent: FC<Props> = ({ imgSizes, editorial, isExpanded }) => {
    const articles = editorial?.articles || [];
    const totalArticlesCount = articles.length;
    if (!totalArticlesCount) {
        return null;
    }

    const elementClassName = classnames(
        styles.articleElement,
        dibsCss.relative,
        dibsCss.flex1,
        dibsCss.flex,
        dibsCss.itemsCenter
    );

    return (
        <HeadingLevel>
            {Heading => (
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.gapMedium,
                        totalArticlesCount > INITIAL_VISIBLE_INTERIOR_ARTICLES
                            ? dibsCss.justifyStart
                            : dibsCss.justifyCenter
                    )}
                >
                    <Heading className={styles.sectionHeader}>
                        <FormattedMessage
                            defaultMessage="From the Magazine"
                            id="abf.SbMobileInteriorsMagazineArticles.header"
                        />
                    </Heading>
                    {articles.map((article, index) => (
                        <HeadingLevel key={`wp-article-container-${index}`}>
                            {() => {
                                const headerId = getHeaderId(index);
                                const { link, title, imageUrl } = article;
                                return (
                                    <Link
                                        ariaLabel={`Read more about ${title}`}
                                        ariaDescribedBy={headerId}
                                        buyerLinkType="standardLinkNoEffect"
                                        underline="none"
                                        href={link}
                                        target="_blank"
                                        dataTn={`wp-article-link-${index}`}
                                        className={classnames(
                                            dibsCss.justifyBetween,
                                            !isExpanded &&
                                                index + 1 > INITIAL_VISIBLE_INTERIOR_ARTICLES
                                                ? dibsCss.hidden
                                                : dibsCss.flex
                                        )}
                                        onClick={() => trackArticleClick(article)}
                                    >
                                        <div
                                            className={classnames(
                                                elementClassName,
                                                styles.imgElement
                                            )}
                                        >
                                            <LazyImage
                                                offsetVertical={LAZY_IMAGE_VERTICAL_OFFSET}
                                                placeholderClass={classnames(
                                                    dibsCss.minWFull,
                                                    dibsCss.minHFull,
                                                    dibsCss.bgAlabaster
                                                )}
                                            >
                                                <img
                                                    className={classnames(
                                                        dibsCss.absolute,
                                                        dibsCss.top0,
                                                        dibsCss.left0,
                                                        dibsCss.wFull,
                                                        dibsCss.hFull,
                                                        dibsCss.objectCover
                                                    )}
                                                    src={imageUrl.thumb}
                                                    alt={title}
                                                    srcSet={`
                                                        ${imageUrl.thumb} ${WORDPRESS_THUMB}w,
                                                        ${imageUrl.medium} ${WORDPRESS_MEDIUM}w,
                                                        ${imageUrl.full} ${WORDPRESS_FULL}w
                                                    `}
                                                    sizes={imgSizes}
                                                />
                                            </LazyImage>
                                        </div>
                                        <div className={elementClassName}>
                                            <Heading
                                                className={classnames(
                                                    styles.header,
                                                    dibsCss.m0,
                                                    dibsCss.textEllipsis
                                                )}
                                                id={headerId}
                                            >
                                                {title}
                                            </Heading>
                                        </div>
                                    </Link>
                                );
                            }}
                        </HeadingLevel>
                    ))}
                </div>
            )}
        </HeadingLevel>
    );
};

export const SbSharedInteriorsArticles = createFragmentContainer(
    SbSharedInteriorsArticlesComponent,
    {
        editorial: graphql`
            fragment SbSharedInteriorsArticles_editorial on WordpressArticlesType {
                articles {
                    publishedDate
                    title
                    imageUrl {
                        thumb
                        medium
                        full
                    }
                    subtitle
                    link
                    source
                }
            }
        `,
    }
);
